import React, { useContext, useEffect, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { useChartTheme } from '../../themes/chartTheme';
import { Box, useTheme, Button } from '@mui/material';
import { tokens } from '../../theme';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import { exportToExcel } from '../../helpers/exportFuncs';
import LoadingSpinner from '../LoadingSpinner';
import moment from 'moment';

interface IAnfoTimelineData {
  date: string;
  anfo_30d: number;
  anfo_total: number;
  leads: number;
}

interface IStreamData {
  [key: string]: number | string;
}

const AnfoTimeline: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const { filterState } = useContext(FilterContext)!;
  const [chartData, setChartData] = useState<IStreamData[]>([]);
  const [originalData, setOriginalData] = useState<IAnfoTimelineData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAnfoData = async (filterState: IFilterState): Promise<IAnfoTimelineData[]> => {
    if (!filterState.startDate || !filterState.endDate || !filterState.network) {
      return [];
    }

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const params = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    return fetchData<IAnfoTimelineData>('/api/vnr/anfo-timeline', params) as Promise<
      IAnfoTimelineData[]
    >;
  };

  const determineAggregation = (
    startDate: Date,
    endDate: Date,
    userSelection: string,
  ): 'daily' | 'weekly' | 'monthly' => {
    if (userSelection !== 'auto') {
      return userSelection as 'daily' | 'weekly' | 'monthly';
    }

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 120) return 'monthly';
    if (diffDays > 45) return 'weekly';
    return 'daily';
  };

  const aggregateData = (
    data: IAnfoTimelineData[],
    aggregationType: 'daily' | 'weekly' | 'monthly',
  ): IAnfoTimelineData[] => {
    if (aggregationType === 'daily') return data;

    const aggregated: { [key: string]: IAnfoTimelineData } = {};

    data.forEach((item) => {
      let key: string;
      const date = moment(item.date);

      if (aggregationType === 'weekly') {
        key = `${date.year()}-W${date.isoWeek().toString().padStart(2, '0')}`;
      } else {
        key = date.format('YYYY-MM');
      }

      if (!aggregated[key]) {
        aggregated[key] = {
          date: key,
          anfo_30d: 0,
          anfo_total: 0,
          leads: 0,
        };
      }

      aggregated[key].anfo_30d += parseFloat(String(item.anfo_30d)) || 0;
      aggregated[key].anfo_total += parseFloat(String(item.anfo_total)) || 0;
      aggregated[key].leads += parseInt(String(item.leads)) || 0;
    });

    return Object.values(aggregated).sort((a, b) => a.date.localeCompare(b.date));
  };

  useEffect(() => {
    const loadData = async () => {
      const hasNetwork = Array.isArray(filterState.network)
        ? filterState.network.length > 0
        : !!filterState.network;

      if (!hasNetwork || !filterState.startDate || !filterState.endDate) {
        return;
      }

      setLoading(true);
      try {
        const rawData = await fetchAnfoData(filterState);

        // Determine aggregation based on date range and user selection
        const aggregationType = determineAggregation(
          filterState.startDate,
          filterState.endDate,
          filterState.vnrAggregation,
        );
        const aggregatedData = aggregateData(rawData, aggregationType);

        // Prepare data for line chart
        const processedData = aggregatedData.map((item) => {
          const anfo30d = parseFloat(String(item.anfo_30d)) || 0;
          const anfoTotal = parseFloat(String(item.anfo_total)) || 0;
          const leads = parseInt(String(item.leads)) || 0;

          return {
            date: item.date,
            'Anfos (30d)': anfo30d,
            Anfos: anfoTotal,
            Leads: leads,
          };
        });

        setChartData(processedData);
        setOriginalData(aggregatedData);
      } catch (error) {
        console.error('Error fetching ANFO timeline data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
    filterState.startDate,
    filterState.endDate,
    filterState.vnrAggregation,
  ]);

  const handleExport = () => {
    const exportData = chartData.map((item) => ({
      Date: item.date,
      'Anfos (30d)': Number(item['Anfos (30d)']),
      Anfos: Number(item['Anfos']),
      Leads: Number(item['Leads']),
    }));
    exportToExcel(exportData, 'ANFO_Timeline');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (chartData.length === 0) {
    return (
      <Box sx={{ margin: 2, textAlign: 'center', color: 'text.secondary' }}>
        <p>No ANFO data available for the selected period and filters.</p>
      </Box>
    );
  }

  return (
    <Box sx={{ margin: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <h2 style={{ margin: 0 }}>VNR ANFO Generation Timeline</h2>
        <Button variant="contained" color="primary" onClick={handleExport}>
          Export to Excel
        </Button>
      </Box>
      <Box sx={{ height: 400 }}>
        <ResponsiveLine
          data={(() => {
            // Find max leads and ANFO to normalize leads scale
            const maxLeads = Math.max(...chartData.map((d) => Number(d.Leads) || 0));
            const maxAnfo = Math.max(
              ...chartData.map((d) =>
                Math.max(Number(d['Anfos (30d)']) || 0, Number(d['Anfos']) || 0),
              ),
            );

            return [
              {
                id: 'Anfos (30d)',
                yAxisId: 'left',
                data: chartData.map((item) => ({
                  x: item.date,
                  y: Number(item['Anfos (30d)']),
                })),
              },
              {
                id: 'Anfos',
                yAxisId: 'left',
                data: chartData.map((item) => ({
                  x: item.date,
                  y: Number(item['Anfos']),
                })),
              },
              {
                id: 'Leads',
                yAxisId: 'right',
                data: chartData.map((item) => {
                  const leads = Number(item.Leads) || 0;
                  // Normalize leads to fit roughly the same scale as ANFOs
                  const normalizedLeads = maxLeads > 0 ? (leads / maxLeads) * maxAnfo : 0;
                  return {
                    x: item.date,
                    y: normalizedLeads,
                  };
                }),
              },
            ];
          })()}
          margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
          axisTop={null}
          axisRight={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Leads',
            legendOffset: 40,
            legendPosition: 'middle',
            format: (value) => {
              if (chartData.length === 0) return value;
              // Find max leads and ANFO to reverse the normalization
              const maxLeads = Math.max(...chartData.map((d) => Number(d.Leads) || 0));
              const maxAnfo = Math.max(
                ...chartData.map((d) =>
                  Math.max(Number(d['Anfos (30d)']) || 0, Number(d['Anfos']) || 0),
                ),
              );
              // Convert normalized value back to actual leads count
              const actualLeads = maxAnfo > 0 ? Math.round((value / maxAnfo) * maxLeads) : 0;
              return actualLeads.toString();
            },
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Time Period',
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'ANFOs',
            legendOffset: -40,
            legendPosition: 'middle',
          }}
          colors={['#1f77b4', '#2ca02c', '#ff7f0e']}
          pointSize={8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          lineWidth={3}
          theme={chartTheme}
          enableGridX={false}
          enableGridY={true}
          useMesh={true}
          tooltip={({ point }) => {
            const value = parseFloat(point.data.yFormatted as string);
            let displayValue;
            let unit;

            if (point.serieId === 'Leads') {
              // Convert normalized value back to actual leads count
              if (chartData.length === 0) {
                displayValue = value.toFixed(0);
                unit = '';
              } else {
                const maxLeads = Math.max(...chartData.map((d) => Number(d.Leads) || 0));
                const maxAnfo = Math.max(
                  ...chartData.map((d) =>
                    Math.max(Number(d['Anfos (30d)']) || 0, Number(d['Anfos']) || 0),
                  ),
                );
                const actualLeads = maxAnfo > 0 ? Math.round((value / maxAnfo) * maxLeads) : 0;
                displayValue = actualLeads.toFixed(0);
                unit = '';
              }
            } else {
              displayValue = value.toLocaleString('de-DE', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              });
              unit = '';
            }

            return (
              <Box
                sx={{
                  padding: '8px 12px',
                  background: colors.primary[400],
                  color: colors.grey[100],
                  borderRadius: 1,
                  boxShadow: 2,
                }}
              >
                <strong>{point.serieId}</strong>: {displayValue}
                {unit}
                <br />
                <small>{`Period: ${point.data.x}`}</small>
              </Box>
            );
          }}
          legends={[
            {
              anchor: 'bottom-right',
              direction: 'column',
              translateX: 100,
              itemWidth: 80,
              itemHeight: 20,
              itemTextColor: colors.grey[100],
              symbolSize: 12,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: colors.grey[900],
                  },
                },
              ],
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default AnfoTimeline;
